import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import i18n from '@/utils/i18n';
import { Routes } from '@/utils/routes';
import { DataTable } from '@/views/Events/EventOwnerDashboard/HandoutsReturnsTable/DataTable';
import { useBreadcrumbs } from '@/components/Breadcrumbs/useBreadcrumbs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InfoIcon } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Communicator } from '../../tools/Communication';
import {
  CanAccessPartnerData,
  ViewerContext,
} from '../../providers/ViewerContextProvider';
import {
  getMilestoneLabel,
  getSimulateWebhooksColumns,
  SimulateWebhookType,
  TestableCheckout,
} from './simulateWebhooksColumns';

const CHECKOUTS_PAGE_SIZE = 20;

/** The webhook simulation the user is about to confirm. */
type PendingSimulation = {
  checkout: TestableCheckout;
  type: SimulateWebhookType;
  daysToReturn?: number;
};

/** Result of Integration/SimulateWebhook (IntegrationWebhookTestResponse). */
type SimulationResult = {
  webhookType: string;
  webhookUrl: string;
  payloadSent: unknown;
  partnerHttpStatus: number | null;
  partnerResponseBody: string | null;
  sendError: string | null;
  durationMs: number;
  hint: string | null;
};

const WEBHOOK_TYPE_LABELS: Record<SimulateWebhookType, () => string> = {
  reminder: () => i18n.t('Integrations:webhookReminder'),
  return: () => i18n.t('Integrations:webhookReturn'),
  invoice: () => i18n.t('Integrations:webhookInvoice'),
};

const SimulateWebhooksPage: React.FC = () => {
  const location = useLocation();
  const viewer = useContext(ViewerContext);
  const canAccessPartnerData = CanAccessPartnerData(viewer);

  const params = new URLSearchParams(location.search);
  const service = params.get('service') ?? '';
  const storeId = params.get('storeId') ?? '';
  // Display name of the integration (custom ones differ from their slug).
  const integrationName = params.get('name') || service;
  // Exact integrations-tab URL the user came from; fall back to the store page.
  const backTo = params.get('from') || Routes.partnerMerchants();

  const [checkouts, setCheckouts] = useState<TestableCheckout[]>([]);
  const [reminderDayMilestones, setReminderDayMilestones] = useState<number[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // The simulation awaiting confirmation (drives the confirm dialog).
  const [pending, setPending] = useState<PendingSimulation | null>(null);
  // Location id of the row whose simulation is in flight (drives the row spinner).
  const [simulatingId, setSimulatingId] = useState<number | null>(null);
  // Outcome of the last simulation (drives the result dialog): the backend's
  // diagnostics on success, or the request's error message when it was rejected.
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [resultError, setResultError] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);

  useBreadcrumbs({
    id: 'simulate-webhooks',
    items: [
      {
        label: i18n.t('Tabs:integrations'),
        href: backTo,
      },
      {
        label: i18n.t('Integrations:simulatePageTitle', {
          name: integrationName,
        }),
        isCurrent: true,
      },
    ],
  });

  const loadCheckouts = useCallback(() => {
    setLoading(true);
    setLoadError(null);

    Communicator.Get('/Integration/GetTestableCheckouts', {
      storeId,
      service,
    })
      .then((response) => response.json())
      .then((data) => {
        setCheckouts(data.checkouts ?? []);
        setReminderDayMilestones(data.reminderDayMilestones ?? []);
        setLoading(false);
      })
      .catch(async (error) => {
        const body =
          error instanceof Response
            ? await error.json().catch(() => null)
            : null;
        setLoadError(
          body?.errorMessage ?? i18n.t('Integrations:simulateLoadError'),
        );
        setLoading(false);
      });
  }, [storeId, service]);

  useEffect(() => {
    loadCheckouts();
  }, [loadCheckouts]);

  const handleSimulate = useCallback(
    (
      checkout: TestableCheckout,
      type: SimulateWebhookType,
      daysToReturn?: number,
    ) => {
      setPending({ checkout, type, daysToReturn });
    },
    [],
  );

  // Runs when the user confirms the dialog: fires the webhook through the
  // portal endpoint and shows the backend's diagnostics in the result dialog.
  const handleSimulateConfirmed = () => {
    const simulation = pending;
    if (!simulation) {
      return;
    }
    setPending(null);
    setSimulatingId(simulation.checkout.containerLocationId);

    Communicator.PostData('/Integration/SimulateWebhook', {
      storeId,
      service,
      webhookType: simulation.type,
      externalUserId: simulation.checkout.externalUserId,
      daysToReturn: simulation.daysToReturn,
      containerIds: [simulation.checkout.containerId],
    })
      .then((response) => response.json())
      .then((data: SimulationResult) => {
        setResult(data);
        setResultError(null);
        setShowResult(true);
        // A reminder simulation moved the checkout's return deadline.
        if (simulation.type === 'reminder') {
          loadCheckouts();
        }
      })
      .catch(async (error) => {
        const body =
          error instanceof Response
            ? await error.json().catch(() => null)
            : null;
        setResult(null);
        setResultError(
          body?.errorMessage ?? i18n.t('Integrations:simulateError'),
        );
        setShowResult(true);
      })
      .finally(() => {
        setSimulatingId(null);
      });
  };

  const columns = useMemo(
    () =>
      getSimulateWebhooksColumns({
        canAccessPartnerData,
        onSimulate: handleSimulate,
        reminderDayMilestones,
        simulatingId,
      }),
    [canAccessPartnerData, handleSimulate, reminderDayMilestones, simulatingId],
  );

  const confirmDescription = pending
    ? {
        reminder: () =>
          i18n.t('Integrations:simulateReminderConfirmDescription', {
            name: pending.checkout.containerName,
            milestone: getMilestoneLabel(pending.daysToReturn ?? 0),
          }),
        return: () =>
          i18n.t('Integrations:simulateReturnConfirmDescription', {
            name: pending.checkout.containerName,
          }),
        invoice: () =>
          i18n.t('Integrations:simulateInvoiceConfirmDescription', {
            name: pending.checkout.containerName,
          }),
      }[pending.type]()
    : '';

  const resultStatusBadge = () => {
    if (!result) {
      return null;
    }
    if (result.sendError) {
      return (
        <Badge variant="destructive">
          {i18n.t('Integrations:simulateResultNotSent')}
        </Badge>
      );
    }
    const isSuccess =
      result.partnerHttpStatus != null &&
      result.partnerHttpStatus >= 200 &&
      result.partnerHttpStatus < 300;
    return (
      <Badge variant={isSuccess ? 'default' : 'destructive'}>
        HTTP {result.partnerHttpStatus}
      </Badge>
    );
  };

  return (
    <div className="tw:mx-auto tw:flex tw:max-w-[1340px] tw:flex-col tw:gap-8 tw:p-6">
      <div className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:flex tw:flex-col tw:gap-1">
          <h2 className="tw:text-2xl tw:font-semibold">
            {i18n.t('Integrations:simulatePageTitle', {
              name: integrationName,
            })}
          </h2>
          <p className="tw:text-muted-foreground tw:text-sm">
            {i18n.t('Integrations:simulatePageDescription')}
          </p>
        </div>
        {/* Webhooks never fire on their own in dev: return/invoice senders only
            run on real events and the reminder cron does not run here, so this
            page is the only way an endpoint receives anything. */}
        <Alert>
          <InfoIcon />
          <AlertDescription>
            {i18n.t('Integrations:simulateManualOnlyNotice')}
          </AlertDescription>
        </Alert>
        <DataTable
          columns={columns}
          data={checkouts}
          isLoading={loading}
          error={loadError}
          opts={{
            pageSize: CHECKOUTS_PAGE_SIZE,
            paginationVariant: 'numbered',
            viewOptionsVariant: 'icon',
          }}
        />
      </div>

      <AlertDialog
        open={!!pending}
        onOpenChange={(open) => {
          if (!open) {
            setPending(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {i18n.t('Integrations:simulateConfirmTitle', {
                type: pending ? WEBHOOK_TYPE_LABELS[pending.type]() : '',
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{i18n.t('Common:cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleSimulateConfirmed}>
              {i18n.t('Integrations:simulateConfirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showResult} onOpenChange={setShowResult}>
        <DialogContent className="tw:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {i18n.t('Integrations:simulateResultTitle')}
            </DialogTitle>
            {result?.hint && (
              <DialogDescription>{result.hint}</DialogDescription>
            )}
          </DialogHeader>
          <DialogBody>
            {resultError ? (
              <p className="tw:text-destructive">{resultError}</p>
            ) : (
              result && (
                <div className="tw:flex tw:flex-col tw:gap-3 tw:text-sm">
                  <div className="tw:flex tw:items-center tw:gap-2">
                    {resultStatusBadge()}
                    <span className="tw:text-muted-foreground">
                      {i18n.t('Integrations:simulateResultDuration', {
                        ms: result.durationMs,
                      })}
                    </span>
                  </div>
                  {result.sendError && (
                    <div>
                      <div className="tw:font-medium">
                        {i18n.t('Integrations:simulateResultSendError')}
                      </div>
                      <p className="tw:text-destructive tw:break-all">
                        {result.sendError}
                      </p>
                    </div>
                  )}
                  <div>
                    <div className="tw:font-medium">
                      {i18n.t('Integrations:simulateResultUrl')}
                    </div>
                    <span className="tw:font-mono tw:text-xs tw:break-all">
                      {result.webhookUrl}
                    </span>
                  </div>
                  <div>
                    <div className="tw:font-medium">
                      {i18n.t('Integrations:simulateResultPayload')}
                    </div>
                    <pre className="tw:bg-muted tw:rounded-md tw:p-2 tw:text-xs tw:overflow-x-auto tw:max-h-64">
                      {JSON.stringify(result.payloadSent, null, 2)}
                    </pre>
                  </div>
                  {result.partnerResponseBody && (
                    <div>
                      <div className="tw:font-medium">
                        {i18n.t('Integrations:simulateResultResponseBody')}
                      </div>
                      <pre className="tw:bg-muted tw:rounded-md tw:p-2 tw:text-xs tw:overflow-x-auto tw:max-h-40">
                        {result.partnerResponseBody}
                      </pre>
                    </div>
                  )}
                </div>
              )
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SimulateWebhooksPage;
